/**
 * auth-session.ts
 * Shared Zod contract for GET /auth/session.
 *
 * This is the SPA's identity source when the JWT lives in an httpOnly cookie:
 * the browser cannot decode the token, so it asks the backend "who am I" and gets
 * back the non-sensitive CLAIMS it needs to drive routing/UI (scope, roles,
 * capabilities, exp) — never the token itself. An XSS payload calling this endpoint
 * learns the claims but cannot steal a bearer credential.
 *
 * Mirrors the frontend SessionClaims discriminated union (tenant | platform).
 *
 * ZERO Hono/Drizzle imports — Zod only.
 */
import { z } from 'zod';

export const TenantSessionResponseSchema = z.object({
  scope: z.literal('tenant'),
  /** Membership id (app_user.id). */
  sub: z.string(),
  /** Company id the session is scoped to. */
  tenantId: z.string(),
  roles: z.array(z.string()),
  capabilities: z.array(z.string()),
  /** JWT expiry (epoch seconds) — lets the SPA detect local expiry. */
  exp: z.number(),
});

export const PlatformSessionResponseSchema = z.object({
  scope: z.literal('platform'),
  sub: z.string(),
  exp: z.number(),
});

/** Discriminated union on `scope`: a session is either tenant or platform. */
export const AuthSessionResponseSchema = z.discriminatedUnion('scope', [
  TenantSessionResponseSchema,
  PlatformSessionResponseSchema,
]);

export type AuthSessionResponse = z.infer<typeof AuthSessionResponseSchema>;
